import { memo, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import filterIcon from "../../../assets/filter.png";
import {
    UploadOutlined,
    SearchOutlined,
    CalendarOutlined,
    UserOutlined,
    MoreOutlined,
    DownloadOutlined,
    DeleteOutlined,
    FilterOutlined,
} from "@ant-design/icons";
import {
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    Empty,
    Form,
    Input,
    message,
    Modal,
    Popconfirm,
    Popover,
    Row,
    Select,
    Space,
    Tag,
    Typography,
} from "antd";
import { Upload } from "antd";
import { PillPagination } from "../../../components/PillPagination";
import DataState from "../../../components/DataState/DataState";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { FileIcon, defaultStyles } from "react-file-icon";
import { InputAdornment, TextField } from "@mui/material";
import { API_BASE_URL } from "../../../config/constants";
import { apiFetch } from "../../../utils/apiClient";
import "../../../styles/trackon.css"


const DEFAULT_CATEGORIES = [
    "Kick Off",
    "Closure",
    "Client Presentation",
    "Others",
];

const CATEGORY_FILTER_OPTIONS = DEFAULT_CATEGORIES.map((category) => ({
    label: category,
    value: category,
}));

const SEARCH_MAX_LENGTH = 100;
const SEARCH_MIN_LENGTH = 3;
const PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 400;
const FILTER_POPUP_WIDTH = 260;

const getFileExtension = (fileName) =>
    fileName.split(".").pop()?.toLowerCase() || "";


const splitFileName = (fileName)=> {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex <= 0) {
        return { baseName: fileName, extension: "" };
    }
    return {
        baseName: fileName.slice(0, lastDotIndex),
        extension: fileName.slice(lastDotIndex),
    };
};

const formatFileSize = (bytes)=> {
    if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const raw = String(dateStr).trim();
    if (!raw || raw === "N/A") return "-";

    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
        const [, year, month, day] = ymd;
        return `${month}-${day}-${year}`;
    }

    const dashDate = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashDate) {
        const [, first, second, year] = dashDate;
        const firstNum = Number(first);
        const secondNum = Number(second);
        if (firstNum > 12 && secondNum <= 12) {
            return `${second}-${first}-${year}`;
        }
        return `${first}-${second}-${year}`;
    }

    const mdy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mdy) {
        const [, month, day, year] = mdy;
        return `${month}-${day}-${year}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        const month = String(parsed.getMonth() + 1).padStart(2, "0");
        const day = String(parsed.getDate()).padStart(2, "0");
        const year = parsed.getFullYear();
        return `${month}-${day}-${year}`;
    }

    return raw;
};

const getAuthHeader = () => {
    const rawToken =
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("accessToken");

    const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

    if (!rawToken) {
        throw new Error("Authentication token not found. Please log in again.");
    }

    const normalizedToken = rawToken
        .replace(/^"|"$/g, "")
        .replace(/^Bearer\s+/i, "")
        .trim();

    return `${tokenType} ${normalizedToken}`;
};

const parseErrorMessage = async (res, fallback) => {
    try {
        const body = await res.json();
        return body?.message || fallback;
    } catch {
        return fallback;
    }
};


const documentsApi = {
    async fetchDocuments(
        projectId,
        params
    ) {
        const query = new URLSearchParams();
        if (params.search) query.set("search", params.search);
        if (params.category) query.set("category", params.category);
        query.set("page", String(params.page));
        query.set("size", String(params.size));

        const res = await apiFetch(`${API_BASE_URL}projects/${projectId}/files/list?${query.toString()}`,
            {
                headers: { Authorization: getAuthHeader() },
            },
            "Failed to load documents"
        );

        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, "Failed to load documents"));
        }

        const body= await res.json();
        return body.data;
    },

    async upload(projectId, category, description, file) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", category);
        formData.append("description", description);

        const res = await apiFetch(`${API_BASE_URL}projects/${projectId}/files/upload`, {
            method: "POST",
            headers: {
                Authorization: getAuthHeader(),
            },
            body: formData,
        }, "File upload failed");

        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, "File upload failed"));
        }
        return res.json().catch(() => null);
    },

    async download(projectId, documentId, fallbackFileName) {
        const res = await apiFetch(
            `${API_BASE_URL}projects/${projectId}/files/download/${documentId}`,
            {
                headers: { Authorization: getAuthHeader() },
            },
            "Download failed"
        );

        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, "Download failed"));
        }

        const disposition = res.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        const fileName = match?.[1] ?? fallbackFileName;
        const blob = await res.blob();
        return { blob, fileName };
    },

    async remove(projectId, documentId) {
        const res = await apiFetch(`${API_BASE_URL}projects/${projectId}/files/${documentId}`, {
            method: "DELETE",
            headers: { Authorization: getAuthHeader() },
        }, "Delete failed");
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, "Delete failed"));
        }
    },

    async rename(projectId, documentId, newFullName) {
        const query = new URLSearchParams({ fileName: newFullName });
        const res = await apiFetch(
            `${API_BASE_URL}projects/${projectId}/files/${documentId}/rename?${query.toString()}`,
            {
                method: "PATCH",
                headers: { Authorization: getAuthHeader() },
            },
            "Rename failed"
        );
        if (!res.ok) {
            throw new Error(await parseErrorMessage(res, "Rename failed"));
        }
        return res.json().catch(() => null);
    },
};

export function ProjectFilesUploadComponent({
    projectId,
    canUpload = true,
    onUploaded,
}) {
    const [form] = Form.useForm();

    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState();

    // Search state
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [showSearchError, setShowSearchError] = useState(false);

    // Filter popover state (Category)
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterData, setFilterData] = useState({});
    const [appliedCategory, setAppliedCategory] = useState();
    const filterContentRef = useRef(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1); // 1-based, for PillPagination

    // Documents state. Each result is tagged with the request key it answered,
    // so a new request (filter, page, search, retry, refresh) is "loading" on its first render.
    const [reloadCount, setReloadCount] = useState(0);
    const [docsResult, setDocsResult] = useState({ key: null, documents: [], totalElements: 0, error: null });

    const activeFilterCount = appliedCategory ? 1 : 0;

    const requestKey = `${projectId}|${currentPage}|${appliedSearch}|${appliedCategory}|${reloadCount}`;
    const hasCurrentResult = docsResult.key === requestKey;
    const listStatus = !hasCurrentResult ? "loading" : docsResult.error ? "error" : "ready";
    const documents = hasCurrentResult ? docsResult.documents : [];
    const totalRecords = hasCurrentResult ? docsResult.totalElements : 0;

    const reloadDocuments = () => setReloadCount((count) => count + 1);

    useEffect(() => {
        let ignore = false;
        const key = requestKey;

        documentsApi
            .fetchDocuments(projectId, {
                search: appliedSearch || undefined,
                category: appliedCategory,
                page: currentPage - 1,
                size: PAGE_SIZE,
            })
            .then((data) => {
                if (ignore) return;
                setDocsResult({
                    key,
                    documents: data.documents ?? [],
                    totalElements: data.totalElements ?? 0,
                    error: null,
                });
            })
            .catch((error) => {
                if (ignore) return;
                setDocsResult({
                    key,
                    documents: [],
                    totalElements: 0,
                    error: error instanceof Error ? error.message : "Unable to load documents",
                });
            });

        return () => {
            ignore = true;
        };
    }, [projectId, currentPage, appliedSearch, appliedCategory, reloadCount]);

    useEffect(() => {
        const trimmed = searchInput.trim();

        if (trimmed.length > 0 && trimmed.length < SEARCH_MIN_LENGTH) {
            setShowSearchError(true);
            return;
        }
        setShowSearchError(false);

        const timeout = setTimeout(() => {
            setAppliedSearch(trimmed);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [searchInput]);

    const clearFilters = () => {
        setFilterData({});
        setAppliedCategory(undefined);
        setCurrentPage(1);
        setFilterOpen(false);
    };

    const applyFilters = () => {
        setAppliedCategory(filterData.category);
        setCurrentPage(1);
        if (filterContentRef.current) {
            filterContentRef.current.scrollTop = 0;
        }
        setFilterOpen(false);
    };

    const resetModal = () => {
        form.resetFields();
        setSelectedFile(null);
        setFileList([]);
        setSelectedCategory(undefined);
    };

    const handleClose = () => {
        if (uploading) return;
        setOpen(false);
        resetModal();
    };

    const handleUpload = async () => {
        try {
            const values = await form.validateFields();

            if (!selectedFile) {
                message.warning("Please select a file to upload");
                return;
            }

            const finalCategory = values.category


            setUploading(true);

            await documentsApi.upload(projectId, finalCategory, values.description || "", selectedFile);

            message.success("Document uploaded successfully");
            setOpen(false);
            resetModal();
            setCurrentPage(1);
            reloadDocuments();
            onUploaded?.();
        } catch (error) {
            if (error instanceof Error) {
                message.error(error.message || "Unable to upload document");
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (documentId, fileName) => {
        try {
            const { blob, fileName: resolvedFileName } = await documentsApi.download(
                projectId,
                documentId,
                fileName
            );
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = resolvedFileName;

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(
                error instanceof Error ? error.message : "Unable to download file"
            );
        }
    };

    const handleDelete = async (documentId) => {
        try {
            await documentsApi.remove(projectId, documentId);
            message.success("Document deleted successfully");

            if (documents.length === 1 && currentPage > 1) {
                setCurrentPage((prev) => prev - 1);
            } else {
                reloadDocuments();
            }
        } catch (error) {
            message.error(
                error instanceof Error ? error.message : "Unable to delete document"
            );
        }
    };

    const handleRename = async (documentId, currentName) => {
        const { baseName, extension } = splitFileName(currentName);
        let newBaseName = baseName;

        Modal.confirm({
            title: "Rename document",
            content: (
                <Input
                    defaultValue={baseName}
                    addonAfter={extension || undefined}
                    onChange={(e) => {
                        newBaseName = e.target.value;
                    }}
                />
            ),
            onOk: async () => {
                const trimmedBase = newBaseName.trim();
                if (!trimmedBase) return;

                const finalName = `${trimmedBase}${extension}`;
                if (finalName === currentName) return;

                try {
                    await documentsApi.rename(projectId, documentId, finalName);
                    message.success("Document renamed");
                    reloadDocuments();
                } catch (error) {
                    message.error(
                        error instanceof Error
                            ? error.message
                            : "Unable to rename document"
                    );
                }
            },
        });
    };

    const filterContent = (
        <div
            className="trackon-root"
            ref={filterContentRef}
            style={{
                width: FILTER_POPUP_WIDTH,
                maxWidth: "calc(100vw - 24px)",
                height: "auto",
                overflow: "visible",
                padding: "4px 0 0",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                }}
            >
                <span style={{ fontWeight: 600, fontSize: 14 }}>Filters</span>

                {activeFilterCount > 0 && (
                    <Button
                        type="link"
                        size="small"
                        onClick={clearFilters}
                        style={{ padding: 0, height: "auto", fontSize: 12 }}
                    >
                        Clear all
                    </Button>
                )}
            </div>

            <div style={{ marginBottom: 14 }}>
                <div
                    style={{
                        fontSize: 12,
                        color: "#595959",
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Category
                </div>

                <Select
                    size="middle"
                    placeholder="None"
                    value={filterData.category}
                    onChange={(value) =>
                        setFilterData((prev) => ({
                            ...prev,
                            category: value,
                        }))
                    }
                    allowClear
                    style={{
                        width: "100%",
                        color: filterData.category ? undefined : "#8c8c8c",
                    }}
                    options={CATEGORY_FILTER_OPTIONS}
                />
            </div>

            <div
                style={{
                    paddingTop: 10,
                    marginTop: 4,
                }}
            >
                <Button
                    type="primary"
                    block
                    onClick={applyFilters}
                    style={{ borderRadius: 8 }}
                >
                    Apply Filters
                </Button>
            </div>
        </div>
    );

    return (
        <Card
            style={{
                borderRadius: 14,
                border: "1px solid #eef1f6",
                boxShadow: "0 4px 14px rgba(16,24,40,0.03)",
            }}
            styles={{ body: { padding: 20 } }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                    width: "100%",
                }}
            >
                <div style={{ flex: "0 0 75%" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            flex: "1 1 320px",
                            minWidth: 220,
                        }}
                    >
                        <div style={{ flex: "1 1 240px", minWidth: 180, maxWidth: 480 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search by name..."
                                value={searchInput}
                                error={showSearchError}
                                inputProps={{
                                    maxLength: SEARCH_MAX_LENGTH,
                                }}
                                onChange={(e) => setSearchInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchOutlined
                                                style={{
                                                    color: "#94a3b8",
                                                    fontSize: 16,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        height: 40,
                                        borderRadius:"4px",
                                        fontSize: 13,
                                        backgroundColor: "#f8fafc",
                                    },
                                }}
                            />

                            <div
                                style={{
                                    minHeight: 18,
                                    marginTop: 4,
                                    color: "#ff0000",
                                    fontSize: 12,
                                }}
                            >
                                {showSearchError
                                    ? `Minimum ${SEARCH_MIN_LENGTH} characters required to search.`
                                    : ""}
                            </div>
                        </div>

                        <Popover
                            content={filterContent}
                            trigger="click"
                            open={filterOpen}
                            onOpenChange={(nextOpen) => {
                                setFilterOpen(nextOpen);
                                if (nextOpen) {
                                    globalThis.setTimeout(() => {
                                        if (filterContentRef.current) {
                                            filterContentRef.current.scrollTop = 0;
                                        }
                                    }, 0);
                                }
                            }}
                            placement="bottomLeft"
                            autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
                            align={{ overflow: { adjustX: 1, adjustY: 1 } }}
                            arrow={false}
                            overlayStyle={{
                                borderRadius: 12,
                                maxWidth: "calc(100vw - 24px)",
                                maxHeight: "calc(100vh - 24px)",
                            }}
                            styles={{
                                root: {
                                    maxWidth: "calc(100vw - 24px)",
                                },
                                body: {
                                    borderRadius: 12,
                                    padding: 12,
                                    width: FILTER_POPUP_WIDTH,
                                    maxWidth: "calc(100vw - 24px)",
                                    overflow: "visible",
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
                                <Button
                                    size="large"
                                    icon={
                                        <img
                                            src={filterIcon}
                                            alt="Filter"
                                            style={{ width: 16, height: 16 }}
                                        />
                                    }
                                    style={{
                                        height: 40,
                                        borderRadius: "4px",
                                        borderColor:
                                            activeFilterCount > 0 ? "#1677ff" : undefined,
                                        color: activeFilterCount > 0 ? "#1677ff" : undefined,
                                        fontWeight: activeFilterCount > 0 ? 600 : undefined,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Filter
                                </Button>
                            </Badge>
                        </Popover>
                    </div>
                </div>

                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    {canUpload && (
                        <Button
                            type="primary"
                            className="trackon-root"
                            icon={<UploadOutlined />}
                            onClick={() => setOpen(true)}
                            style={{
                                height: 40,
                                width:"auto",
                                borderRadius: "4px",
                                backgroundColor:"#0958d9",
                                fontWeight: 600,
                                flexShrink: 0,
                                marginLeft: "auto",
                                boxShadow: "0 6px 16px rgba(22, 93, 255, 0.22)",
                            }}
                        >
                            Upload Document
                        </Button>
                    )}
                </div>
            </div>

            <DataState
                status={listStatus}
                errorTitle="Couldn't load documents"
                error={docsResult.error}
                onRetry={reloadDocuments}
                skeletonRows={6}
            >
            <>
            {/* Section title */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                }}
            >
                <Typography.Text strong style={{ fontSize: 16, color: "#3c3c3e" }}>
                    Documents
                </Typography.Text>
                <Typography.Text style={{ fontSize: 14, color: "#94a3b8" }}>
                    {totalRecords} file{totalRecords === 1 ? "" : "s"}
                </Typography.Text>
            </div>

            {/* Fixed min-height keeps the card from collapsing/expanding
                between the empty and populated states. */}
            <div style={{ minHeight: 220 }}>
                {documents.length === 0 ? (
                    <Empty
                        description="No documents uploaded yet"
                        style={{ padding: "20px 0" }}
                    />
                ) : (
                    <Row gutter={[16, 16]}>
                        {documents.map((doc) => {

                            const menuItems = [
                                {
                                    key: "download",
                                    label: "Download",
                                    icon: <DownloadOutlined />,
                                    onClick: () =>
                                        handleDownload(doc.documentId, doc.fileName),
                                },
                                {
                                    key: "rename",
                                    label: "Rename",
                                    icon: <EditOutlinedIcon style={{ fontSize: 15 }} />,
                                    onClick: () =>
                                        handleRename(doc.documentId, doc.fileName),
                                },
                                {
                                    key: "delete",
                                    label: (
                                        <Popconfirm
                                            title="Delete document"
                                            description="Are you sure you want to delete this document?"
                                            okText="Delete"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => handleDelete(doc.documentId)}
                                        >
                                            <span style={{ color: "#f5222d" }}>Delete</span>
                                        </Popconfirm>
                                    ),
                                    icon: <DeleteOutlined style={{ color: "#f5222d" }} />,
                                },
                            ];

                            return (
                                <Col
                                    xs={24}
                                    sm={12}
                                    md={12}
                                    lg={8}
                                    xl={6}
                                    key={doc.documentId}
                                >
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: 14,
                                            border: "1px solid #e5e7eb",
                                            boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
                                            height: "100%",
                                        }}
                                        styles={{
                                            body: {
                                                padding: 14,
                                                display: "flex",
                                                flexDirection: "column",
                                                height: "100%",
                                            },
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                                gap: 8,
                                                marginBottom: 12,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 23,
                                                        height: 23,
                                                        flexShrink: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <FileIcon
                                                        extension={getFileExtension(doc.fileName)}
                                                        {...defaultStyles[
                                                        getFileExtension(doc.fileName)
                                                        ]}
                                                    />
                                                </div>

                                                <div
                                                    title={doc.fileName}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        color: "#1f2937",
                                                        lineHeight: "18px",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {splitFileName(doc.fileName).baseName}
                                                </div>
                                            </div>
                                            <Dropdown
                                                menu={{ items: menuItems }}
                                                trigger={["click"]}
                                                placement="bottomRight"
                                            >
                                                <Button
                                                    type="text"
                                                    shape="circle"
                                                    size="small"
                                                    icon={
                                                        <MoreOutlined
                                                            style={{
                                                                fontSize: 18,
                                                                color: "#94a3b8",
                                                            }}
                                                        />
                                                    }
                                                    style={{ flexShrink: 0 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Dropdown>
                                        </div>

                                        <div
                                            style={{
                                                borderTop: "1px solid #f1f5f9",
                                                paddingTop: 10,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    marginBottom: 6,
                                                    color: "#334155",
                                                    fontSize: 12.5,
                                                }}
                                            >
                                                <CalendarOutlined
                                                    style={{ color: "#94a3b8", fontSize: 13 }}
                                                />
                                                <span>{formatDate(doc.uploadedDate)}</span>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    marginBottom: 10,
                                                    color: "#334155",
                                                    fontSize: 12.5,
                                                }}
                                            >
                                                <UserOutlined
                                                    style={{ color: "#94a3b8", fontSize: 13 }}
                                                />
                                                <span
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {doc.uploadedBy}
                                                </span>
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    gap: 8,
                                                }}
                                            >
                                                <Tag
                                                    color="blue"
                                                    style={{
                                                        borderRadius: 999,
                                                        margin: 0,
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        maxWidth: "65%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {doc.category}
                                                </Tag>

                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#94a3b8",
                                                        fontWeight: 500,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {formatFileSize(doc.fileSize)}
                                                </span>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>

            {totalRecords > 0 && (
                <PillPagination
                    currentPage={currentPage}
                    pageSize={PAGE_SIZE}
                    total={totalRecords}
                    itemLabel="documents"
                    onPageChange={setCurrentPage}
                />
            )}

            </>
            </DataState>

            <Modal
                title="Upload Project Document"
                className="trackon-root"
                open={open}
                onCancel={handleClose}
                width="min(520px, 92vw)"
                destroyOnClose
                footer={
                    <Space>
                        <Button onClick={handleClose} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            style={{ color:"white" }}
                            loading={uploading}
                            onClick={handleUpload}
                            disabled={!selectedFile}
                        >
                            Upload
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical" style={{ marginTop: 14 }}>
                    <Form.Item
                        label="Category"
                        name="category"
                        className="trackon-root"
                        rules={[{ required: true, message: "Please select category" }]}
                    >
                        <Select
                            placeholder="Select Category"
                            value={selectedCategory}
                            onChange={(value) => {
                                setSelectedCategory(value);
                                if (value !== "Others") {
                                    form.setFieldValue("description", undefined);
                                }
                            }}
                            options={DEFAULT_CATEGORIES.map((category) => ({
                                label: category,
                                value: category,
                            }))}
                        />
                    </Form.Item>

                    {selectedCategory === "Others" && (
                        <Form.Item
                            label="Description"
                            name="description"
                            rules={[{ required: false, message: "Please enter description" }]}
                        >
                            <Input placeholder="Enter description" />
                        </Form.Item>
                    )}

                    <Form.Item label="File" required>
                        <Upload
                            maxCount={1}
                            fileList={fileList}
                            beforeUpload={(file) => {
                                setSelectedFile(file);
                                setFileList([file]);
                                return false;
                            }}
                            onRemove={() => {
                                setSelectedFile(null);
                                setFileList([]);
                            }}
                        >
                            <Button icon={<UploadOutlined />}>Select File</Button>
                        </Upload>

                        <Typography.Text
                            type="secondary"
                            style={{ display: "block", marginTop: 8, fontSize: 12 }}
                        >
                            Allowed formats - pdf, ppt, pptx, xls, xlsx, doc, docx, csv, json, txt, xml, html, htm
                        </Typography.Text>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
}

export const ProjectFilesUpload = memo(ProjectFilesUploadComponent);